import { EyeOutlined } from '@ant-design/icons';
import {
  Alert,
  Button,
  Checkbox,
  Collapse,
  Modal,
  Select,
  Tag,
  Tooltip,
  message,
} from 'antd';
import dayjs from 'dayjs';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import CertificateAPI from '../../certificate/CertificateAPI';
import { OpenClass, OpenClassRegistration } from '../types/OpenClass';
import { OpenClassCertificateEmailResult } from '../types/certificateEmail';
import { certificateEmailOutcome } from '../utils/certificateEmailOutcome';
import {
  ExcludedCandidate,
  SendCandidate,
  SendGroup,
  buildCertificateSendPlan,
  canReceive,
  defaultSelection,
  selectionCounts,
} from '../utils/certificateSendPlan';
import { certificateTypeLabel } from '../utils/certificateTypeLabel';
import { ClassAttendeeSession } from '../utils/openClassAttendees';
import OpenClassSessionStatusTags from './OpenClassSessionStatusTags';
import BesaccCertificatePreviewModal, {
  BesaccCertificatePreview,
} from './BesaccCertificatePreviewModal';

type OpenClassSendCertificatesModalProps = {
  open: boolean;
  openClass: OpenClass;
  registrations: OpenClassRegistration[];
  onClose: () => void;
  onCompleted: () => Promise<void> | void;
};

const roundTag = 'm-0 rounded-full border-0';

const Recipient: React.FC<{
  candidate: SendCandidate;
}> = ({ candidate }) => {
  const { t } = useTranslation();

  if (candidate.destination === 'own') {
    return <span className="text-xs text-grayMedium">{candidate.email}</span>;
  }
  return (
    <span
      className={`text-xs ${
        candidate.destination === 'contact'
          ? 'text-safeLifeIncomplete'
          : 'text-warningRed'
      }`}>
      {t(
        candidate.destination === 'contact'
          ? 'components.OpenClassSendCertificates.toContactPerson'
          : 'components.OpenClassSendCertificates.noRecipient',
      )}
    </span>
  );
};

const CountTag: React.FC<{ count: number }> = ({ count }) => (
  <Tag className={`${roundTag} bg-blueGrayLightest text-grayMedium`}>
    {count}
  </Tag>
);

const AttendeeRow: React.FC<{
  children?: React.ReactNode;
  name: React.ReactNode;
  recipient: React.ReactNode;
  sessions: ClassAttendeeSession[];
  sessionFooter?: React.ReactNode;
  select?: React.ReactNode;
}> = ({ children, name, recipient, sessions, sessionFooter, select }) => (
  <div className="flex items-center gap-3 border-0 border-b border-solid border-grayLight/40 py-2 last:border-b-0">
    {select}
    <div className="flex min-w-0 flex-1 flex-col">
      <span className="truncate">{name}</span>
      {recipient}
    </div>
    <div className="flex flex-col items-end gap-1">
      <OpenClassSessionStatusTags className="justify-end" sessions={sessions} />
      {sessionFooter}
    </div>
    {children}
  </div>
);

const OpenClassSendCertificatesModal: React.FC<
  OpenClassSendCertificatesModalProps
> = ({ open, openClass, registrations, onClose, onCompleted }) => {
  const { t } = useTranslation();
  const [ticks, setTicks] = useState<{ sheet: string; ids: Set<number> }>();
  const [languageByTemplate, setLanguageByTemplate] = useState<
    Record<number, number | undefined>
  >({});
  const [submitting, setSubmitting] = useState(false);
  const [certificatePreview, setCertificatePreview] =
    useState<BesaccCertificatePreview>();

  const plan = useMemo(
    () => buildCertificateSendPlan(openClass, registrations),
    [openClass, registrations],
  );

  const awaitingSend = defaultSelection(plan);
  const sheet = [...awaitingSend].join(',');
  const selected = ticks?.sheet === sheet ? ticks.ids : awaitingSend;
  const setSelected = (update: (current: Set<number>) => Set<number>) =>
    setTicks((current) => ({
      sheet,
      ids: update(current?.sheet === sheet ? current.ids : awaitingSend),
    }));

  const close = () => {
    setTicks(undefined);
    onClose();
  };

  useEffect(() => {
    setLanguageByTemplate((current) =>
      Object.fromEntries(
        plan.groups.map(({ template }) => [
          template.id,
          current[template.id] ?? template.languages[0]?.id,
        ]),
      ),
    );
  }, [plan.groups]);

  const counts = selectionCounts(plan, selected);

  const toggle = (registrationId: number) =>
    setSelected((current) => {
      const next = new Set(current);
      if (next.has(registrationId)) next.delete(registrationId);
      else next.add(registrationId);
      return next;
    });

  const toggleGroup = (group: SendGroup, checked: boolean) =>
    setSelected((current) => {
      const next = new Set(current);
      group.candidates.forEach((candidate) => {
        if (checked && canReceive(candidate) && !candidate.sentAt) {
          next.add(candidate.registrationId);
        }
        if (!checked) next.delete(candidate.registrationId);
      });
      return next;
    });

  const submit = async () => {
    setSubmitting(true);
    const emailResults: OpenClassCertificateEmailResult[] = [];
    let failedEmails = 0;

    for (const group of plan.groups) {
      const ids = group.candidates
        .filter(({ registrationId }) => selected.has(registrationId))
        .map(({ registrationId }) => registrationId);
      if (ids.length === 0) continue;

      try {
        emailResults.push(
          ...(await CertificateAPI.emailOpenClassCertificates(
            openClass.id,
            ids,
            group.template.id,
            languageByTemplate[group.template.id],
            true,
          )),
        );
      } catch {
        failedEmails += ids.length;
      }
    }

    const {
      sent,
      sentToContactPerson,
      alreadySent,
      noEmail,
      missingCertificate,
      failed,
    } = certificateEmailOutcome(emailResults);
    const delivered = sent + sentToContactPerson;
    const problems =
      alreadySent + noEmail + missingCertificate + failed + failedEmails;
    const text = [
      delivered > 0
        ? t('components.OpenClassSendCertificates.resultMailed', {
            count: delivered,
          })
        : null,
      problems > 0
        ? t('components.OpenClassSendCertificates.resultFailed', {
            count: problems,
          })
        : null,
    ]
      .filter(Boolean)
      .join('. ');

    if (problems === 0) message.success(text);
    else if (delivered > 0) message.warning(text);
    else message.error(text || t('views.OpenClasses.emailCertificatesFailed'));

    try {
      await onCompleted();
    } finally {
      setSubmitting(false);
      // A partial result is worth reading, so the sheet only closes once it all landed.
      if (problems === 0) close();
    }
  };

  const groupPanels = plan.groups.map((group) => {
    const selectable = group.candidates.filter(
      (candidate) => canReceive(candidate) && !candidate.sentAt,
    );
    const picked = selectable.filter(({ registrationId }) =>
      selected.has(registrationId),
    ).length;
    const languages = group.template.languages;

    return {
      key: String(group.template.id),
      label: (
        <span className="flex items-center gap-2">
          <span className="font-medium">
            {certificateTypeLabel(group.template.type)}
          </span>
          <CountTag count={group.candidates.length} />
          <span className="text-xs text-grayMedium">
            {t('components.OpenClassSendCertificates.groupEmail')}
          </span>
        </span>
      ),
      extra: languages.length > 0 && (
        <Select
          disabled={submitting}
          options={languages.map(({ id, name }) => ({
            label: name,
            value: id,
          }))}
          size="small"
          style={{ minWidth: 120 }}
          value={languageByTemplate[group.template.id]}
          onChange={(languageId) =>
            setLanguageByTemplate((current) => ({
              ...current,
              [group.template.id]: languageId,
            }))
          }
          onClick={(event) => event.stopPropagation()}
        />
      ),
      children: (
        <>
          <div className="border-0 border-b border-solid border-grayLight/40 pb-2">
            <Checkbox
              checked={picked > 0 && picked === selectable.length}
              disabled={submitting || selectable.length === 0}
              indeterminate={picked > 0 && picked < selectable.length}
              onChange={({ target }) => toggleGroup(group, target.checked)}>
              <span className="text-xs text-grayMedium">
                {t('components.OpenClassSendCertificates.selectAll')}
              </span>
            </Checkbox>
          </div>
          {group.candidates.map((candidate: SendCandidate) => (
            <AttendeeRow
              key={candidate.registrationId}
              name={candidate.name}
              recipient={
                <span className="flex items-center gap-2">
                  <Recipient candidate={candidate} />
                  {candidate.attendanceOnly && (
                    <Tag
                      className={`${roundTag} bg-offwhiteDark text-xs font-medium text-safeLifeIncomplete`}>
                      {t(
                        'components.OpenClassSendCertificates.attendanceAttest',
                      )}
                    </Tag>
                  )}
                </span>
              }
              select={
                <Checkbox
                  checked={selected.has(candidate.registrationId)}
                  disabled={submitting || !canReceive(candidate)}
                  onChange={() => toggle(candidate.registrationId)}
                />
              }
              sessionFooter={
                candidate.sentAt && (
                  <Tooltip
                    title={
                      candidate.sentTo &&
                      t(
                        'components.OpenClassAttendeeActions.sentToContactPerson',
                        { email: candidate.sentTo },
                      )
                    }>
                    <span className="whitespace-nowrap text-xs text-grayMedium">
                      {t('components.OpenClassSendCertificates.sentAt', {
                        date: dayjs(candidate.sentAt).format(
                          'YYYY-MM-DD HH:mm',
                        ),
                      })}
                    </span>
                  </Tooltip>
                )
              }
              sessions={candidate.sessions}>
              {candidate.certificateFileName ? (
                <Tooltip
                  title={t('components.BesaccCertificatePreview.preview')}>
                  <Button
                    aria-label={t(
                      'components.BesaccCertificatePreview.preview',
                    )}
                    icon={<EyeOutlined />}
                    size="small"
                    type="text"
                    onClick={() =>
                      setCertificatePreview({
                        attendeeName: candidate.name,
                        fileName: candidate.certificateFileName!,
                        registrationId: candidate.registrationId,
                        sentAt: candidate.sentAt,
                      })
                    }
                  />
                </Tooltip>
              ) : null}
            </AttendeeRow>
          ))}
        </>
      ),
    };
  });

  const excludedPanel =
    plan.excluded.length > 0
      ? [
          {
            key: 'excluded',
            label: (
              <span className="flex items-center gap-2">
                <span className="font-medium text-grayDark">
                  {t('components.OpenClassSendCertificates.excludedHeading')}
                </span>
                <CountTag count={plan.excluded.length} />
              </span>
            ),
            children: plan.excluded.map((candidate: ExcludedCandidate) => (
              <AttendeeRow
                key={candidate.registrationId}
                name={<span className="text-grayDark">{candidate.name}</span>}
                recipient={
                  <span className="text-xs text-grayMedium">
                    {candidate.email}
                  </span>
                }
                sessions={candidate.sessions}>
                <Tag
                  className={`${roundTag} ${
                    candidate.reason === 'failed'
                      ? 'bg-offwhiteDark text-warningRed'
                      : 'bg-blueGrayLightest text-grayMedium'
                  }`}>
                  {t(
                    `components.OpenClassSendCertificates.reason.${candidate.reason}`,
                  )}
                </Tag>
              </AttendeeRow>
            )),
          },
        ]
      : [];

  return (
    <>
      <Modal
        closable={!submitting}
        footer={
          <div className="flex items-center justify-between gap-4">
            <span className="text-left text-xs text-grayMedium">
              {counts.alreadySent > 0 &&
                t('components.OpenClassSendCertificates.alreadySentNote', {
                  count: counts.alreadySent,
                })}
            </span>
            <div className="flex gap-2">
              <Button disabled={submitting} onClick={close}>
                {t('common.cancel')}
              </Button>
              <Button
                disabled={counts.mailed === 0}
                loading={submitting}
                type="primary"
                onClick={submit}>
                {t('components.OpenClassSendCertificates.submit', {
                  mailed: counts.mailed,
                })}
              </Button>
            </div>
          </div>
        }
        keyboard={!submitting}
        maskClosable={!submitting}
        open={open}
        title={t('components.OpenClassSendCertificates.title')}
        width={720}
        onCancel={close}>
        {plan.excluded.some(({ reason }) => reason === 'missingCertificate') ? (
          <Alert
            className="mb-4"
            message={t('components.OpenClassSendCertificates.missingWarning')}
            showIcon
            type="warning"
          />
        ) : null}
        <Collapse
          // What still has something to send leads, the excluded list is one click away.
          defaultActiveKey={groupPanels.map(({ key }) => key)}
          expandIconPosition="start"
          items={[...groupPanels, ...excludedPanel]}
        />
      </Modal>
      <BesaccCertificatePreviewModal
        certificate={certificatePreview}
        onClose={() => setCertificatePreview(undefined)}
      />
    </>
  );
};

export default OpenClassSendCertificatesModal;
